/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { withoutIndex, withValueByIndex } from '@gh/core-util';
import { iteratee, sortBy, sortedIndex, sortedIndexOf } from 'lodash';

/**
 * Model for selection. This model supports 'Select All' mode.
 * If <code>isExcludeMode = false</code> <code>idList</code> specifies list of selected entities,
 * otherwise if <code>isExcludeMode = true</code> <code>idList</code> specifies list of not selected entities.
 */
export interface SelectionModel {
  isExcludeMode: boolean;
  idList: any[];
}

/**
 * Returns model for empty selection
 */
export const emptySelectionModel = (isExcludeMode: boolean = false) => <SelectionModel>({ isExcludeMode, idList: [] });

/**
 * Returns model for provided IDs
 */
export const newSelectionModel = (isExcludeMode: boolean, idList: any[]) => <SelectionModel>({
  isExcludeMode,
  idList: sortBy(idList),
});

/**
 * Creates predicate for given selection model,
 * which defines whether entity passed into predicate is included into selection.
 * In order to define whether entity is included into selection, developer has to instruct how to get entity ID.
 *
 * @param model {@link SelectionModel}
 * @param id defines identity property of entity or getter for identity
 */
export const bySelectionModel = <T>(model: SelectionModel, id: (string | TransformFn<T, string>)): PredicateFn<T> => {
  const getId = iteratee(id);
  if (model.isExcludeMode) {
    return (item) => sortedIndexOf(model.idList, getId(item)) < 0;
  } else {
    return (item) => sortedIndexOf(model.idList, getId(item)) >= 0;
  }
};

/**
 * Returns true if model defines empty selection.
 * <b>Note!!!</b> It is impossible to define whether actual selection is empty if <code>isExcludeMode = true</code>,
 * because we do not know how much entities exist.
 */
export const isSelectionModelEmpty = ({ isExcludeMode, idList }: SelectionModel) =>
  isExcludeMode === false && idList.length === 0;

/**
 * Returns true if model is in exclude mode.
 */
export const isSelectionModelExcludeMode = ({ isExcludeMode }: SelectionModel) => isExcludeMode;

/**
 * Answers if given id is selected by the selection model
 */
export const isSelectionModelHas = (model: SelectionModel, id: any): boolean => {
  if (model.isExcludeMode) {
    return sortedIndexOf(model.idList, id) < 0;
  } else {
    return sortedIndexOf(model.idList, id) >= 0;
  }
};

/**
 * Guarantees that only specified id is selected, otherwise returns empty model
 */
export const selectionModelSelectOnly = (model: SelectionModel, id: any, select: boolean): SelectionModel => {
  if (select) {
    return { isExcludeMode: false, idList: [id] };
  } else if (model.idList[0] === id) {
    return { isExcludeMode: false, idList: [] };
  } else {
    return model;
  }
};

/**
 * Updates selection state for the provided ID
 */
export const selectionModelSelect = (model: SelectionModel, id: any, select: boolean): SelectionModel => {
  const { idList } = model;

  if (model.isExcludeMode) {
    if (select) {
      const index = sortedIndexOf(idList, id);
      if (index >= 0) {
        return { isExcludeMode: true, idList: withoutIndex(idList, index) };
      } else {
        return model;
      }
    } else {
      const index = sortedIndex(idList, id);
      if (idList[index] === id) {
        return model;
      } else {
        return { isExcludeMode: true, idList: withValueByIndex(idList, index, id) };
      }
    }
  } else if (select) {
    const index = sortedIndex(idList, id);
    if (idList[index] === id) {
      return model;
    } else {
      return { isExcludeMode: false, idList: withValueByIndex(idList, index, id) };
    }
  } else {
    const index = sortedIndexOf(idList, id);
    if (index >= 0) {
      return { isExcludeMode: false, idList: withoutIndex(idList, index) };
    } else {
      return model;
    }
  }
};

/**
 * Selects all provided IDs
 */
export const selectionModelSelectAll = (model: SelectionModel, ids: any[]): SelectionModel =>
  ids.reduce((lastModel, id) => selectionModelSelect(lastModel, id, true), model);

/**
 * Returns number of selected entities when <code>isExcludeMode = false</code>.
 * If <code>isExcludeMode = true</code> throws Error, because it is impossible to define number of selected entities.
 */
export const selectionModelSize = (model: SelectionModel): number => {
  if (model.isExcludeMode) {
    throw Error('SelectionModel: size cannot be determined in exclude mode');
  } else {
    return model.idList.length;
  }
};

/**
 * Returns number of selected entities for include mode or number of unselected entities for exclude mode.
 */
export const selectionModelMarkedCount = (model: SelectionModel): number => model.idList.length;

/**
 * Returns all IDs of selected entities when <code>isExcludeMode = false</code>.
 * If <code>isExcludeMode = true</code> throws Error, because it is impossible to define all selected IDs.
 */
export const selectionModelGetIds = (model: SelectionModel): any[] => {
  if (model.isExcludeMode) {
    throw Error('SelectionModel: IDs cannot be determined in exclude mode');
  } else {
    return model.idList;
  }
};

export const selectionModelAfterRowRemoved = (
  model: SelectionModel,
  id: any,
  isGridDataEmptyAfterRemoval: boolean = false,
): SelectionModel => {
  if (isGridDataEmptyAfterRemoval) {
    return emptySelectionModel();
  }
  if (!model.isExcludeMode) {
    return selectionModelSelect(model, id, false);
  }
  const index = sortedIndexOf(model.idList, id);
  if (index < 0) {
    return model;
  }
  return { isExcludeMode: true, idList: withoutIndex(model.idList, index) };
};
